import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckSquare,
  ClipboardCheck,
  FileText,
  Search,
  Target,
  Users,
} from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import { formatNr2jDate, nr2jStageLabel } from '../../utils/nr2jConstants';
import {
  MY_TEAM_STATUT_FILTERS,
  NO_MANAGER_FILTER,
  aggregateTeamKpis,
  aggregateTeamPipeline,
  buildTeamAlerts,
  filterTeamMembers,
  listManagersFromMembers,
  memberDisplayName,
  memberInitials,
  memberStatutBadge,
  myTeamLabels,
} from '../../utils/nr2jMyTeam.js';
import { readAdminSessionUser } from '../../utils/settingsSessionUser.js';

const ALERT_TONES = {
  high: 'border-rose-200 bg-rose-50 text-rose-900',
  medium: 'border-amber-200 bg-amber-50 text-amber-900',
  low: 'border-slate-200 bg-slate-50 text-slate-800',
};

function KpiTile({ title, value, hint, icon: Icon, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    emerald: 'border-emerald-200 bg-emerald-50',
    indigo: 'border-indigo-200 bg-indigo-50',
  };
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tones[tone] || tones.slate}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="mt-1 text-3xl font-semibold text-gray-900">{value ?? 0}</p>
          {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
        </div>
        {Icon ? <Icon className="h-6 w-6 text-gray-500" /> : null}
      </div>
    </div>
  );
}

function StatCell({ value, tone, className = '' }) {
  const n = Number(value) || 0;
  const cls = n > 0 && tone === 'rose'
    ? 'font-semibold text-rose-700'
    : n > 0 && tone === 'amber'
      ? 'font-semibold text-amber-700'
      : 'text-gray-700';
  return <td className={`px-3 py-3 text-center tabular-nums ${cls} ${className}`}>{n}</td>;
}

function sumStat(members, key) {
  return members.reduce((total, m) => total + (Number(m.stats?.[key]) || 0), 0);
}

export default function Nr2jMyTeam() {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [session] = useState(() => readAdminSessionUser());
  const isSuperAdmin = session?.role === 'SUPER_ADMIN';
  const labels = myTeamLabels(isSuperAdmin);

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [managerId, setManagerId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [statut, setStatut] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listNr2jMyTeam();
      setMembers(res?.members || []);
      try {
        const queue = await apiService.listNr2jApprovals();
        setApprovals(queue?.approvals || []);
      } catch {
        setApprovals([]);
      }
    } catch (error) {
      toast.error(error?.message || `Impossible de charger ${labels.title}`);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const managers = useMemo(() => listManagersFromMembers(members), [members]);
  const commercialOptions = useMemo(
    () => [...members].sort((a, b) => memberDisplayName(a).localeCompare(memberDisplayName(b), 'fr')),
    [members],
  );

  const filtered = useMemo(() => {
    const base = filterTeamMembers(members, {
      managerId: isSuperAdmin ? managerId : '',
      statut,
      q,
    });
    return memberId ? base.filter((m) => m.id === memberId) : base;
  }, [members, isSuperAdmin, managerId, statut, q, memberId]);

  const kpis = useMemo(() => aggregateTeamKpis(filtered), [filtered]);
  const pipeline = useMemo(() => aggregateTeamPipeline(filtered), [filtered]);
  const quotesOpenTotal = useMemo(() => sumStat(filtered, 'quotesOpen'), [filtered]);
  const tasksOpenTotal = useMemo(() => sumStat(filtered, 'tasksOpen'), [filtered]);
  const alerts = useMemo(() => buildTeamAlerts(filtered, approvals), [filtered, approvals]);
  const hasFilters = Boolean(managerId || memberId || statut || q.trim());

  function resetFilters() {
    setManagerId('');
    setMemberId('');
    setStatut('');
    setQ('');
  }

  return (
    <div className="min-w-0 space-y-6 overflow-x-hidden" data-testid="nr2j-my-team">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{labels.title}</h1>
          <p className="text-sm text-gray-600">{labels.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/nr2j/a-valider"
            className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            <ClipboardCheck className="h-4 w-4" />
            À valider{approvals.length ? ` (${approvals.length})` : ''}
          </Link>
          <Link
            to="/prospection"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Target className="h-4 w-4" />
            Prospection
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
          Chargement…
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="nr2j-my-team-kpis">
            <KpiTile
              title="Commerciaux actifs"
              value={kpis.activeMembers}
              hint={`sur ${filtered.length} dans la vue`}
              icon={Users}
              tone="indigo"
            />
            <KpiTile title="Prospects actifs" value={kpis.prospectsActive} icon={Target} />
            <KpiTile
              title="Devis à valider"
              value={kpis.quotesPendingApproval}
              hint={approvals.length ? `file À valider : ${approvals.length}` : undefined}
              icon={ClipboardCheck}
              tone="amber"
            />
            <KpiTile
              title="Tâches en retard"
              value={kpis.tasksOverdue}
              hint={tasksOpenTotal ? `sur ${tasksOpenTotal} tâches en cours` : undefined}
              icon={CheckSquare}
              tone="rose"
            />
            <KpiTile
              title="Devis en cours"
              value={quotesOpenTotal}
              icon={FileText}
              tone="emerald"
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <section
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
              data-testid="nr2j-my-team-alerts"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Alertes prioritaires
                </h2>
                {kpis.quotesPendingApproval > 0 ? (
                  <span className="text-xs text-gray-500">
                    {kpis.quotesPendingApproval} devis en attente
                  </span>
                ) : null}
              </div>
              {alerts.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500">Aucune alerte — tout est à jour.</p>
              ) : (
                <ul className="mt-4 space-y-2">
                  {alerts.map((alert) => (
                    <li key={alert.id}>
                      <Link
                        to={alert.href}
                        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 hover:shadow-sm ${
                          ALERT_TONES[alert.severity] || ALERT_TONES.low
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{alert.title}</p>
                          {alert.detail ? (
                            <p className="truncate text-xs opacity-80">{alert.detail}</p>
                          ) : null}
                          {alert.memberName ? (
                            <p className="mt-0.5 truncate text-xs font-medium opacity-90">
                              {alert.memberName}
                            </p>
                          ) : null}
                        </div>
                        <ArrowRight className="h-4 w-4 flex-shrink-0 opacity-60" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-my-team-pipeline">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Pipeline équipe</h2>
                <Link to="/prospection" className="text-xs font-medium text-indigo-700 hover:underline">
                  Voir tous les prospects
                </Link>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                {pipeline.map((row) => (
                  <Link
                    key={row.stage}
                    to={`/prospection?stage=${encodeURIComponent(row.stage)}`}
                    className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 hover:border-indigo-200 hover:bg-indigo-50"
                  >
                    <p className="text-xs text-gray-500">{nr2jStageLabel(row.stage)}</p>
                    <p className="text-lg font-semibold text-gray-900">{row.count}</p>
                  </Link>
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Total : {pipeline.reduce((sum, row) => sum + row.count, 0)} prospects
              </p>
            </section>
          </div>

          <div className="flex flex-wrap items-center gap-3" data-testid="nr2j-my-team-filters">
            <div className="relative min-w-0 max-w-md flex-1 basis-[220px]">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un commercial…"
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
              />
            </div>
            {isSuperAdmin ? (
              <select
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                data-testid="nr2j-my-team-manager-filter"
              >
                <option value="">Tous les managers</option>
                <option value={NO_MANAGER_FILTER}>Sans manager</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            ) : null}
            <select
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              data-testid="nr2j-my-team-member-filter"
            >
              <option value="">Tous les commerciaux</option>
              {commercialOptions.map((m) => (
                <option key={m.id} value={m.id}>{memberDisplayName(m)}</option>
              ))}
            </select>
            <select
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              data-testid="nr2j-my-team-statut-filter"
            >
              {MY_TEAM_STATUT_FILTERS.map((f) => (
                <option key={f.id || 'all'} value={f.id}>{f.label}</option>
              ))}
            </select>
            {hasFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs font-medium text-gray-500 hover:text-indigo-700"
              >
                Réinitialiser
              </button>
            ) : null}
          </div>

          <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                {labels.title}
              </h2>
              <span className="text-xs text-gray-500">{filtered.length} collaborateur{filtered.length > 1 ? 's' : ''}</span>
            </div>
            {filtered.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="mx-auto h-8 w-8 text-gray-300" />
                <p className="mt-3 text-sm text-gray-600">
                  {members.length === 0
                    ? (isSuperAdmin
                      ? 'Aucun commercial NR2J pour le moment.'
                      : 'Aucun commercial direct dans votre équipe.')
                    : 'Aucun commercial pour ces filtres.'}
                </p>
              </div>
            ) : (
              <table className="w-full table-fixed divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="w-[34%] px-4 py-3">Collaborateur</th>
                    <th className="w-[10%] px-2 py-3 text-center">Prospects</th>
                    <th className="w-[10%] px-2 py-3 text-center">À relancer</th>
                    <th className="w-[12%] px-2 py-3 text-center">Tâches en retard</th>
                    <th className="w-[9%] px-2 py-3 text-center">Devis</th>
                    <th className="w-[9%] px-2 py-3 text-center">À valider</th>
                    <th className="hidden w-[10%] px-3 py-3 xl:table-cell">Dernière activité</th>
                    <th className="w-[6%] px-2 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((member) => {
                    const badge = memberStatutBadge(member.statut);
                    const stats = member.stats || {};
                    const managerLabel = member.managerName || 'Sans manager';
                    return (
                      <tr
                        key={member.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/nr2j/mon-equipe/${member.id}`)}
                        data-testid="nr2j-my-team-row"
                      >
                        <td className="min-w-0 px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700"
                              aria-hidden="true"
                            >
                              {memberInitials(member)}
                            </span>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-gray-900">
                                {memberDisplayName(member)}
                              </div>
                              <div className="truncate text-xs text-gray-500">
                                Commercial · {badge.label}
                                {isSuperAdmin ? ` · ${managerLabel}` : ''}
                              </div>
                              <div className="truncate text-xs text-gray-400">{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <StatCell value={stats.prospectsActive} />
                        <StatCell value={stats.prospectsFollowUp} tone="amber" />
                        <StatCell value={stats.tasksOverdue} tone="rose" />
                        <StatCell value={stats.quotesOpen} />
                        <StatCell value={stats.quotesPendingApproval} tone="amber" />
                        <td className="hidden px-3 py-3 text-xs text-gray-500 xl:table-cell">
                          {member.lastActivityAt ? formatNr2jDate(member.lastActivityAt) : '—'}
                        </td>
                        <td className="px-2 py-3 text-right">
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
                            data-testid="nr2j-my-team-open"
                          >
                            Voir la fiche <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
